<template>
  <div class="orb-business-panel">
    <!-- 折叠状态：紧凑标题栏 -->
    <div v-if="collapsed" class="collapsed-bar">
      <div class="collapsed-info" @click="expandPanel">
        <span class="collapsed-icon">📋</span>
        <span class="collapsed-title">{{ activeScenarioInfo.name }}防汛应急联动方案</span>
        <span class="collapsed-badge" :class="sheetSourceClass">{{ currentSheet ? formatRiskLevel(currentSheet.risk_level) : '加载中' }}</span>
      </div>
      <div class="collapsed-actions">
        <button class="collapsed-btn" @click="expandPanel">展开 ▲</button>
        <button class="collapsed-btn close-btn" @click="closePanel">✕</button>
      </div>
    </div>

    <!-- 展开状态：完整面板 -->
    <div v-else class="border">
      <div class="panel-head">
        <p class="card-title">
          <img src="../assets/titleL.png" alt="" />&nbsp;{{ activeScenarioInfo.name }}防汛应急联动方案&nbsp;
          <img src="../assets/titleR.png" alt="" />
        </p>
        <button class="close" type="button" @click="collapsePanel" title="折叠">—</button>
      </div>

      <div class="content-body">
        <!-- 场景选择器 -->
        <div class="scenario-selector-bar">
          <label class="scenario-label">应急场景：</label>
          <select class="scenario-select" :value="activeScenarioKey" @change="onScenarioChange($event.target.value)">
            <option v-for="s in scenarioList" :key="s.key" :value="s.key">{{ s.name }}（{{ s.river }}）- {{ s.region }}</option>
          </select>
        </div>
        <div class="status-strip" :class="sheetSourceClass">
          <span>{{ sourceLabel }}</span>
          <strong>{{ currentSheet ? formatRiskLevel(currentSheet.risk_level) : '加载中' }}</strong>
        </div>

        <section class="section gis-section">
          <div class="section-title">GIS 联动图层</div>
          <div class="gis-summary">
            <div>
              <strong>{{ gisData.releaseCorridor.name }}</strong>
              <span>{{ gisData.releaseCorridor.warning }}</span>
              <span v-if="gisData.floodAnimation" style="color:#ff6b35;margin-top:4px;display:block">
                <strong>洪水演进动画已启用</strong>：中心 {{ gisData.floodAnimation.centerLng.toFixed(3) }}°E, {{ gisData.floodAnimation.centerLat.toFixed(3) }}°N
              </span>
            </div>
            <ul>
              <li>下泄走廊 {{ gisData.releaseCorridor.positions.length }} 点折线</li>
              <li>响应断面 {{ gisData.responseSections.length }} 个</li>
              <li>风险点 {{ gisData.riskPoints.length }} 个</li>
              <li>转移路线 {{ gisData.routes.length }} 条</li>
              <li>避险/安置点 {{ gisData.shelters.length }} 个</li>
              <li>物资点 {{ gisData.materialPoints.length }} 个</li>
              <li>管控分区 {{ gisData.zones.length }} 个</li>
              <li>转移人数 {{ gisData.metadata.totalEvacuationPeople }} 人</li>
            </ul>
          </div>
          <div class="gis-toolbar">
            <button type="button" class="gis-jump-btn" @click="flyToGisMap">跳转 GIS 地图</button>
            <button type="button" class="gis-jump-btn" @click="toggleGisDetail">{{ showGisDetail ? '收起详情' : '展开详情' }}</button>
          </div>
        </section>

        <section v-if="showGisDetail" class="section gis-detail-section">
          <div class="section-title">响应断面（3 个）</div>
          <div class="gis-detail-grid">
            <div v-for="section in gisData.responseSections" :key="section.id" class="gis-detail-card">
              <strong>{{ section.name }}</strong>
              <span>坐标：{{ section.lng.toFixed(4) }}°E, {{ section.lat.toFixed(4) }}°N</span>
              <span>级别：{{ section.level }}</span>
              <span>监测站：{{ section.station }}</span>
              <span>警戒水位：{{ section.warningLevel }} / 设计：{{ section.designLevel }}</span>
              <span>响应时限：{{ section.responseTime }}</span>
              <p>{{ section.action }}</p>
            </div>
          </div>

          <div class="section-title" style="margin-top:12px">风险点（5 个）</div>
          <div class="gis-detail-grid">
            <div v-for="point in gisData.riskPoints" :key="point.id" class="gis-detail-card" :class="'risk-' + point.zone">
              <strong>{{ point.name }}</strong>
              <span>坐标：{{ point.lng.toFixed(4) }}°E, {{ point.lat.toFixed(4) }}°N</span>
              <span>类型：{{ point.type }} / 人数：{{ point.people }} 人</span>
              <span>超阈：{{ point.exceedance }} m / {{ point.priority }}</span>
              <span>联系人：{{ point.contact }}</span>
              <p>{{ point.action }}</p>
            </div>
          </div>

          <div class="section-title" style="margin-top:12px">避险点（5 个）</div>
          <div class="gis-detail-grid">
            <div v-for="shelter in gisData.shelters" :key="shelter.id" class="gis-detail-card">
              <strong>{{ shelter.name }}</strong>
              <span>坐标：{{ shelter.lng.toFixed(4) }}°E, {{ shelter.lat.toFixed(4) }}°N</span>
              <span>等级：{{ shelter.level }}</span>
              <span>容量：{{ shelter.capacity }} 人 / 负责人：{{ shelter.manager }}</span>
              <p>{{ shelter.supplies }}</p>
              <em>{{ shelter.register }}</em>
            </div>
          </div>

          <div class="section-title" style="margin-top:12px">物资点（4 个）</div>
          <div class="gis-detail-grid">
            <div v-for="mat in gisData.materialPoints" :key="mat.id" class="gis-detail-card">
              <strong>{{ mat.name }}</strong>
              <span>坐标：{{ mat.lng.toFixed(4) }}°E, {{ mat.lat.toFixed(4) }}°N</span>
              <span>物资：{{ mat.material }}</span>
              <span>管理员：{{ mat.manager }}</span>
              <p>去向：{{ mat.target }}</p>
            </div>
          </div>

          <div class="section-title" style="margin-top:12px">转移路线（4 条）</div>
          <div class="gis-detail-grid">
            <div v-for="route in gisData.routes" :key="route.id" class="gis-detail-card">
              <strong>{{ route.name }}</strong>
              <span>起点：{{ route.start }}</span>
              <span>终点：{{ route.end }}</span>
              <span>步行 {{ route.walkMinutes }} 分钟 / {{ route.distanceKm }} km / 容量 {{ route.capacity }} 人</span>
              <span>守控：{{ route.guard }}</span>
              <p>轨迹点：{{ route.positions.length }} 个坐标</p>
            </div>
          </div>

          <div class="section-title" style="margin-top:12px">管控分区（4 个）</div>
          <div class="gis-detail-grid">
            <div v-for="zone in gisData.zones" :key="zone.id" class="gis-detail-card" :class="'zone-detail-' + zone.id.split('-')[1]">
              <strong>{{ zone.name }}</strong>
              <span>规则：{{ zone.rule }}</span>
              <span>动作：{{ zone.action }}</span>
              <p>多边形顶点：{{ zone.positions.length }} 个坐标</p>
            </div>
          </div>
        </section>

        <section class="section overview-section">
          <div class="section-title">应急事件</div>
          <div v-if="loading" class="empty-line">正在生成应急处置单...</div>
          <div v-else-if="currentEvent" class="event-summary">
            <div>
              <span class="muted">事件编号</span>
              <strong>{{ formatEventId(currentEvent.event_id) }}</strong>
            </div>
            <div>
              <span class="muted">来源场景</span>
              <strong>{{ formatScenarioName(currentEvent.source_scenario_id) }}</strong>
            </div>
            <div>
              <span class="muted">当前状态</span>
              <strong>{{ formatStatus(currentEvent.status) }}</strong>
            </div>
          </div>
          <div v-if="errorMessage" class="error-line">{{ errorMessage }}</div>
        </section>

        <section class="section">
          <div class="section-title">应急处置单</div>
          <div v-if="currentSheet" class="sheet-grid">
            <div class="metric">
              <span>预案</span>
              <strong>{{ currentSheet.plan_name }}</strong>
            </div>
            <div class="metric">
              <span>责任人</span>
              <strong>{{ formatOwner(currentSheet.owner) }}</strong>
            </div>
            <div class="metric">
              <span>风险对象</span>
              <strong>{{ riskObjects.length }}</strong>
            </div>
            <div class="metric">
              <span>转移人数</span>
              <strong>{{ evacuationTotal }} 人</strong>
            </div>
          </div>
          <div class="toolbar">
            <button type="button" @click="refreshSheet" :disabled="loading">重新生成</button>
            <button type="button" @click="exportSheet" :disabled="!currentSheet">导出处置单</button>
            <button type="button" @click="generateDrill" :disabled="!currentSheet || loading">生成演练方案</button>
            <button type="button" @click="exportDrill" :disabled="!currentDrill">导出演练方案</button>
            <button type="button" class="task-network-btn" @click="openTaskNetwork">任务网络与证据</button>
          </div>
        </section>

        <section v-if="currentDrill" class="section">
          <div class="section-title">演练方案</div>
          <div class="sheet-grid">
            <div class="metric">
              <span>方案</span>
              <strong>{{ currentDrill.title }}</strong>
            </div>
            <div class="metric">
              <span>状态</span>
              <strong>{{ currentDrill.status }}</strong>
            </div>
            <div class="metric">
              <span>演练步骤</span>
              <strong>{{ currentDrill.steps.length }}</strong>
            </div>
            <div class="metric">
              <span>评估项</span>
              <strong>{{ currentDrill.evaluation.length }}</strong>
            </div>
          </div>
        </section>

        <div v-if="currentSheet" class="section edit-box">
          <label>
            <span>责任人</span>
            <input v-model="ownerDraft" />
          </label>
          <label>
            <span>处置措施（每行一条）</span>
            <textarea v-model="actionsDraft" rows="4"></textarea>
          </label>
          <div class="toolbar">
            <button type="button" @click="saveSheetEdits" :disabled="loading || currentSheet.source !== 'api'">保存编辑</button>
            <button type="button" @click="changeSheetStatus('reviewed')" :disabled="loading || currentSheet.source !== 'api'">复核</button>
            <button type="button" @click="changeSheetStatus('issued')" :disabled="loading || currentSheet.source !== 'api'">发布</button>
            <button type="button" @click="changeSheetStatus('completed')" :disabled="loading || currentSheet.source !== 'api'">完成</button>
          </div>
        </div>

        <section class="section two-col">
          <div>
            <div class="section-title">风险对象</div>
            <ul class="compact-list">
              <li v-for="item in riskObjects" :key="`${item.name}-${item.type}`">
                <strong>{{ item.name }}</strong>
                <span>{{ item.type }} / {{ item.people || 0 }} 人 / 超阈 {{ item.exceedance_m || 0 }} m</span>
                <em v-if="item.priority">{{ item.priority }} · {{ item.contact }}</em>
              </li>
            </ul>
          </div>
          <div>
            <div class="section-title">处置措施</div>
            <ol class="action-list">
              <li v-for="action in actions" :key="action">{{ action }}</li>
            </ol>
          </div>
        </section>

        <section class="section">
          <div class="section-title">红橙黄蓝分区与管控动作</div>
          <div class="zone-grid">
            <div v-for="zone in controlZones" :key="zone.zone" :class="['zone-card', zoneClass(zone.zone)]">
              <strong>{{ zone.zone }}</strong>
              <span>{{ zone.rule }}</span>
              <p>{{ zone.action }}</p>
            </div>
          </div>
        </section>

        <section class="section two-col">
          <div>
            <div class="section-title">触发条件</div>
            <div class="condition-list">
              <div v-for="item in triggerConditions" :key="item.level" class="condition-card">
                <strong>{{ item.level }}</strong>
                <span>{{ item.rule }}</span>
              </div>
            </div>
          </div>
          <div>
            <div class="section-title">城镇基础设施</div>
            <div class="condition-list">
              <div v-for="item in infrastructure" :key="item.name" class="condition-card">
                <strong>{{ item.name }}</strong>
                <span>{{ item.type }} · {{ item.role }}</span>
              </div>
            </div>
          </div>
        </section>

        <section class="section">
          <div class="section-title">人员转移路线</div>
          <div class="route-list">
            <div v-for="route in evacuationRoutes" :key="route.name" class="route-card">
              <div class="route-main">
                <strong>{{ route.name }}</strong>
                <span>{{ route.start }} -> {{ route.end }}</span>
                <p>{{ route.via }}</p>
              </div>
              <div class="route-meta">
                <span>{{ route.shelter_level }}</span>
                <b>{{ route.walk_minutes }} 分钟</b>
                <em>{{ route.distance_km }} km / 容量 {{ route.capacity }} 人</em>
              </div>
              <div class="route-guard">守控：{{ route.guard }}；备用：{{ route.backup }}</div>
            </div>
          </div>
        </section>

        <section class="section">
          <div class="section-title">避险点与安置能力</div>
          <div class="shelter-grid">
            <div v-for="shelter in shelters" :key="shelter.name" class="shelter-card">
              <strong>{{ shelter.name }}</strong>
              <span>{{ shelter.level }} · 容量 {{ shelter.capacity }} 人 · 负责人 {{ shelter.manager }}</span>
              <p>{{ shelter.supplies }}</p>
              <em>{{ shelter.register }}</em>
            </div>
          </div>
        </section>

        <section class="section two-col">
          <div>
            <div class="section-title">强制动作链</div>
            <div class="timeline-list">
              <div v-for="step in warningChain" :key="`${step.minute}-${step.actor}`" class="timeline-item">
                <b>T+{{ step.minute }}'</b>
                <div>
                  <strong>{{ step.actor }}</strong>
                  <span>{{ step.action }}</span>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div class="section-title">通信与失效兜底</div>
            <div class="communication-list">
              <div v-for="item in communications" :key="item.name" class="communication-item">
                <strong>{{ item.name }}：{{ item.tool }}</strong>
                <span>持有人：{{ item.holder }}</span>
                <em>兜底：{{ item.fallback }}</em>
              </div>
            </div>
          </div>
        </section>

        <section class="section two-col">
          <div>
            <div class="section-title">队伍建议</div>
            <table class="data-table">
              <tr>
                <th>队伍</th>
                <th>负责人</th>
                <th>人数</th>
                <th>电话</th>
              </tr>
              <tr v-for="team in teams" :key="team.name">
                <td>{{ team.name }}<br><span class="table-sub">{{ team.duty }}</span></td>
                <td>{{ team.leader }}</td>
                <td>{{ team.members }}</td>
                <td>{{ team.phone }}</td>
              </tr>
            </table>
          </div>
          <div>
            <div class="section-title">物资分配</div>
            <table class="data-table">
              <tr>
                <th>物资</th>
                <th>数量</th>
                <th>位置与去向</th>
              </tr>
              <tr v-for="item in materials" :key="item.name">
                <td>{{ item.name }}<br><span class="table-sub">管理员：{{ item.manager }}</span></td>
                <td>{{ item.count }} {{ item.unit }}</td>
                <td>{{ item.place }}<br><span class="table-sub">{{ item.target }}</span></td>
              </tr>
            </table>
          </div>
        </section>

        <section class="section">
          <div class="section-title">相似事件</div>
          <div class="similar-list">
            <div v-for="item in similarEvents" :key="item.event_id" class="similar-item">
              <strong>{{ item.title }}</strong>
              <span>{{ formatRiskLevel(item.risk_level) }} / 关联度 {{ item.score }}</span>
              <p>{{ item.summary }}</p>
            </div>
          </div>
        </section>

        <section v-if="exportContent" class="section">
          <div class="section-title">导出预览</div>
          <pre class="export-box">{{ exportContent }}</pre>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import {
  FALLBACK_SCENARIO,
  FALLBACK_SHEET,
  createEmergencySheetFromScenario,
  exportEmergencyDrillPlanOrFallback,
  exportEmergencySheetOrFallback,
  generateEmergencyDrillPlanOrFallback,
  transitionEmergencySheet,
  updateEmergencySheet,
  switchScenario,
  listScenarios,
} from '@/services/emergencyService.js'
import { getActiveScenario, getActiveScenarioInfo, getActiveScenarioKey } from '@/services/emergencyMapData.js'

export default {
  name: 'emergencyManagement',
  props: {
    recommendedPlan: {
      type: String,
      default: '',
    },
    sourceWarning: {
      type: String,
      default: '',
    },
    scenarioResult: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      currentEvent: null,
      currentSheet: null,
      currentDrill: null,
      exportContent: '',
      ownerDraft: '',
      actionsDraft: '',
      gisData: getActiveScenario(),
      scenarioList: listScenarios(),
      activeScenarioKey: getActiveScenarioKey(),
      showGisDetail: true,
      collapsed: false,
    }
  },
  computed: {
    activeScenarioInfo() {
      const s = this.scenarioList.find(s => s.key === this.activeScenarioKey)
      return s || { name: '龙门镇禹门口', river: '黄河', region: '陕西韩城' }
    },
    sourceLabel() {
      if (!this.currentSheet) return '应急数据初始化中'
      return this.currentSheet.source === 'api' ? '后端应急流程' : '本地应急资料'
    },
    sheetSourceClass() {
      return this.currentSheet && this.currentSheet.source === 'api' ? 'api' : 'fallback'
    },
    riskObjects() {
      return this.currentSheet && Array.isArray(this.currentSheet.risk_objects) ? this.currentSheet.risk_objects : []
    },
    actions() {
      return this.currentSheet && Array.isArray(this.currentSheet.actions) ? this.currentSheet.actions : []
    },
    teams() {
      return this.currentSheet && Array.isArray(this.currentSheet.teams) ? this.currentSheet.teams : []
    },
    triggerConditions() {
      return this.currentSheet && Array.isArray(this.currentSheet.trigger_conditions) ? this.currentSheet.trigger_conditions : []
    },
    infrastructure() {
      return this.currentSheet && Array.isArray(this.currentSheet.infrastructure) ? this.currentSheet.infrastructure : []
    },
    materials() {
      return this.currentSheet && Array.isArray(this.currentSheet.materials) ? this.currentSheet.materials : []
    },
    evacuationRoutes() {
      return this.currentSheet && Array.isArray(this.currentSheet.evacuation_routes) ? this.currentSheet.evacuation_routes : []
    },
    shelters() {
      return this.currentSheet && Array.isArray(this.currentSheet.shelters) ? this.currentSheet.shelters : []
    },
    warningChain() {
      return this.currentSheet && Array.isArray(this.currentSheet.warning_chain) ? this.currentSheet.warning_chain : []
    },
    communications() {
      return this.currentSheet && Array.isArray(this.currentSheet.communications) ? this.currentSheet.communications : []
    },
    controlZones() {
      return this.currentSheet && Array.isArray(this.currentSheet.control_zones) ? this.currentSheet.control_zones : []
    },
    similarEvents() {
      return this.currentSheet && Array.isArray(this.currentSheet.similar_events) ? this.currentSheet.similar_events : []
    },
    evacuationTotal() {
      return this.riskObjects.reduce((sum, item) => sum + (Number(item.people) || 0), 0)
    },
  },
  mounted() {
    this.refreshSheet()
  },
  methods: {
    closeClick() {
      this.$emit('infoClose')
    },
    collapsePanel() {
      this.collapsed = true
    },
    expandPanel() {
      this.collapsed = false
    },
    closePanel() {
      this.collapsed = false
      this.$emit('infoClose')
    },
    flyToGisMap() {
      this.$emit('flyToGis', getActiveScenario().camera)
    },
    onScenarioChange(key) {
      switchScenario(key)
      this.activeScenarioKey = key
      this.gisData = getActiveScenario()
      const info = getActiveScenarioInfo()
      this.$message.success(`已切换至 ${info.name}（${info.river}）场景`)
      // 重新加载当前场景的处置单数据
      this.refreshSheet()
      this.$emit('scenarioChanged', key)
    },
    toggleGisDetail() {
      this.showGisDetail = !this.showGisDetail
    },
    zoneClass(zone) {
      if (zone.includes('红')) return 'zone-red'
      if (zone.includes('橙')) return 'zone-orange'
      if (zone.includes('黄')) return 'zone-yellow'
      return 'zone-blue'
    },
    formatRiskLevel(level) {
      const map = { red: '红色预警', orange: '橙色预警', yellow: '黄色预警', blue: '蓝色预警' }
      return map[level] || level || '待研判'
    },
    formatStatus(status) {
      const map = {
        created: '已创建',
        draft: '草案编制中',
        reviewed: '已复核',
        issued: '已发布',
        completed: '已完成',
        sheet_generated: '处置单已生成',
        sheet_reviewed: '处置单已复核',
        sheet_issued: '处置单已发布',
        sheet_completed: '处置闭环',
      }
      return map[status] || status || '待处置'
    },
    formatEventId(id) {
      const info = getActiveScenarioInfo()
      const prefix = `${info.name}防汛联动事件`
      if (!id) return prefix
      const match = String(id).match(/(\d+)$/)
      return match ? `${prefix}第 ${Number(match[1])} 号` : prefix
    },
    formatScenarioName(name) {
      const info = getActiveScenarioInfo()
      const defaultName = `${info.name}洪水应急联动场景`
      if (!name) return defaultName
      // 如果是当前场景的 scenario_id 或包含 demo/frontend，返回默认名
      const currentId = FALLBACK_SCENARIO.scenario_id
      if (name === currentId || /demo|frontend/i.test(name)) return defaultName
      return String(name).replace(/demo/ig, '').replace(/frontend/ig, '').replace(/[-_]+$/g, '') || defaultName
    },
    formatOwner(owner) {
      const fallbackOwner = FALLBACK_SHEET.owner
      if (!owner || /frontend|emergency-duty-user/i.test(owner)) return fallbackOwner
      return owner
    },
    async refreshSheet() {
      this.loading = true
      this.errorMessage = ''
      this.exportContent = ''
      try {
        const scenario = this.scenarioResult || this.buildScenarioFromProps()
        const result = await createEmergencySheetFromScenario(scenario, {
          title: this.normalizeTitle(this.sourceWarning),
          owner: FALLBACK_SHEET.owner,
        })
        this.currentEvent = result.event
        this.currentSheet = result.sheet
        this.currentDrill = null
        this.syncDrafts()
      } catch (error) {
        this.errorMessage = error.message || '应急处置单生成失败'
      } finally {
        this.loading = false
      }
    },
    syncDrafts() {
      this.ownerDraft = this.formatOwner(this.currentSheet?.owner)
      this.actionsDraft = Array.isArray(this.currentSheet?.actions) ? this.currentSheet.actions.join('\n') : ''
    },
    async saveSheetEdits() {
      if (!this.currentSheet || this.currentSheet.source !== 'api') return
      this.loading = true
      this.errorMessage = ''
      try {
        const updated = await updateEmergencySheet(this.currentSheet.sheet_id, {
          owner: this.ownerDraft,
          actions: this.actionsDraft.split('\n').map(item => item.trim()).filter(Boolean),
        })
        this.currentSheet = { ...updated, source: 'api' }
        this.syncDrafts()
      } catch (error) {
        this.errorMessage = error.message || '处置单保存失败'
      } finally {
        this.loading = false
      }
    },
    async changeSheetStatus(status) {
      if (!this.currentSheet || this.currentSheet.source !== 'api') return
      this.loading = true
      this.errorMessage = ''
      try {
        const updated = await transitionEmergencySheet(this.currentSheet.sheet_id, {
          status,
          actor: this.ownerDraft || '古贤枢纽防汛值班长',
          comment: `处置单状态调整为${this.formatStatus(status)}`,
        })
        this.currentSheet = { ...updated, source: 'api' }
        this.syncDrafts()
        if (this.currentEvent) {
          this.currentEvent = { ...this.currentEvent, status: `sheet_${status}` }
        }
      } catch (error) {
        this.errorMessage = error.message || '处置单状态流转失败'
      } finally {
        this.loading = false
      }
    },
    async exportSheet() {
      const result = await exportEmergencySheetOrFallback(this.currentSheet)
      this.exportContent = result.content
    },
    async generateDrill() {
      if (!this.currentSheet) return
      this.loading = true
      this.errorMessage = ''
      try {
        this.currentDrill = await generateEmergencyDrillPlanOrFallback(this.currentSheet, {
          owner: this.ownerDraft || this.currentSheet.owner,
        })
      } catch (error) {
        this.errorMessage = error.message || '演练方案生成失败'
      } finally {
        this.loading = false
      }
    },
    async exportDrill() {
      const result = await exportEmergencyDrillPlanOrFallback(this.currentDrill)
      this.exportContent = result.content
    },
    openTaskNetwork() {
      const info = getActiveScenarioInfo()
      this.$emit('openTaskExecution', {
        source: 'emergencyPlan',
        businessId: this.currentSheet && (this.currentSheet.sheet_id || this.currentSheet.plan_id) || `EMERGENCY-${this.activeScenarioKey}`,
        planName: this.currentSheet && this.currentSheet.plan_name,
        planStatus: this.currentSheet && this.currentSheet.status,
        scenario: info.name,
        region: info.region,
        riskLevel: this.currentSheet && /red|orange/i.test(this.currentSheet.risk_level) ? 'HIGH' : 'LOW',
        actions: this.actions,
      })
    },
    buildScenarioFromProps() {
      if (!this.recommendedPlan) return FALLBACK_SCENARIO
      return {
        ...FALLBACK_SCENARIO,
        recommended_plan: {
          ...FALLBACK_SCENARIO.recommended_plan,
          name: this.recommendedPlan,
        },
      }
    },
    normalizeTitle(title) {
      const info = getActiveScenarioInfo()
      const defaultTitle = `${info.name}防汛应急联动处置`
      if (!title || /演示|demo|frontend/i.test(title)) return defaultTitle
      return title
    },
  },
}
</script>

<style scoped>
.orb-business-panel {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 9998;
}

/* ── 折叠状态样式 ── */
.collapsed-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 18px;
  background: linear-gradient(135deg, rgba(5, 24, 32, 0.95), rgba(13, 30, 39, 0.92));
  border: 1px solid rgba(99, 179, 237, 0.28);
  border-radius: 10px;
  box-shadow: 0 10px 36px rgba(0, 0, 0, 0.42);
  cursor: pointer;
  backdrop-filter: blur(8px);
}

.collapsed-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.collapsed-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.collapsed-title {
  font-size: 15px;
  font-weight: bold;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.collapsed-badge {
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.collapsed-badge.api {
  background: rgba(20, 184, 166, 0.22);
  color: #99f6e4;
}

.collapsed-badge.fallback {
  background: rgba(245, 158, 11, 0.22);
  color: #fde68a;
}

.collapsed-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.collapsed-btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid rgba(56, 189, 248, 0.32);
  color: #b8ecff;
  background: rgba(56, 189, 248, 0.12);
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
  transition: background 0.2s;
}

.collapsed-btn:hover {
  background: rgba(56, 189, 248, 0.22);
}

.collapsed-btn.close-btn {
  border-color: rgba(248, 113, 113, 0.42);
  background: rgba(248, 113, 113, 0.14);
  color: #fecaca;
}

.collapsed-btn.close-btn:hover {
  background: rgba(248, 113, 113, 0.26);
}

.border {
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(8, 18, 32, 0.96), rgba(13, 38, 50, 0.94));
  border-radius: 10px;
  padding: 18px;
  box-sizing: border-box;
  color: #eef7ff;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(99, 179, 237, 0.28);
  box-shadow: 0 18px 52px rgba(0, 0, 0, 0.42);
}

.panel-head {
  position: relative;
  min-height: 42px;
}

.card-title {
  text-align: center;
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  margin: 4px 0 10px;
}

.card-title img {
  vertical-align: middle;
}

.close {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, border-color 0.2s;
}
.close:hover {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.28);
}

.content-body {
  color: #fff;
  font-size: 14px;
  padding: 0 12px 12px;
  height: calc(100% - 42px);
  overflow-y: auto;
  overflow-x: hidden;
}

.scenario-selector-bar {
display: flex;
align-items: center;
gap: 8px;
padding: 6px 10px;
margin-bottom: 6px;
background: rgba(56, 189, 248, 0.08);
border: 1px solid rgba(56, 189, 248, 0.2);
border-radius: 6px;
}

.scenario-label {
font-size: 13px;
color: #7dd3fc;
white-space: nowrap;
}

.scenario-select {
flex: 1;
padding: 4px 8px;
font-size: 13px;
color: #e0f2fe;
background: rgba(8, 18, 32, 0.9);
border: 1px solid rgba(56, 189, 248, 0.3);
border-radius: 4px;
outline: none;
cursor: pointer;
}

.scenario-select:hover {
border-color: rgba(56, 189, 248, 0.6);
}

.scenario-select option {
background: #08121f;
color: #e0f2fe;
}

.status-strip {
display: flex;
justify-content: space-between;
align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.status-strip.api {
  background: rgba(20, 184, 166, 0.14);
  color: #99f6e4;
}

.status-strip.fallback {
  background: rgba(245, 158, 11, 0.14);
  color: #fde68a;
}

.section {
  margin-top: 14px;
}

.gis-section {
  padding: 10px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(11, 48, 58, 0.72), rgba(17, 34, 44, 0.78));
  border: 1px solid rgba(80, 216, 225, 0.22);
  box-shadow: inset 0 0 18px rgba(60, 180, 190, 0.08);
}

.gis-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
}

.gis-summary strong {
  display: block;
  color: #ffffff;
  margin-bottom: 4px;
}

.gis-summary span {
  color: #bdebef;
  line-height: 1.6;
}

.gis-summary ul {
  display: grid;
  grid-template-columns: repeat(2, minmax(88px, 1fr));
  gap: 6px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.gis-summary li {
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(7, 34, 43, 0.72);
  border: 1px solid rgba(120, 220, 228, 0.14);
  color: #d6f7fa;
  white-space: nowrap;
}

.section-title {
  font-size: 14px;
  margin: 0 0 8px;
  color: #fff;
  border-left: 4px solid rgba(56, 189, 248, 0.48);
  padding-left: 8px;
  font-weight: 700;
}

.event-summary,
.sheet-grid,
.two-col,
.zone-grid,
.shelter-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.event-summary {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.zone-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.event-summary > div,
.metric,
.similar-item,
.zone-card,
.shelter-card,
.route-card,
.communication-item,
.condition-card,
.timeline-item {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 10px;
}

.muted,
.metric span,
.similar-item span {
  display: block;
  color: rgba(255, 255, 255, 0.56);
  font-size: 12px;
  margin-bottom: 4px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.toolbar button {
  min-width: 96px;
  padding: 7px 12px;
  border-radius: 6px;
  border: 1px solid rgba(56, 189, 248, 0.32);
  color: #b8ecff;
  background: rgba(56, 189, 248, 0.12);
  cursor: pointer;
}

.toolbar button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.edit-box {
  padding: 10px;
  border: 1px solid rgba(99, 179, 237, 0.28);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.16);
}

.edit-box label {
  display: block;
  margin-bottom: 8px;
}

.edit-box label span {
  display: block;
  color: rgba(255, 255, 255, 0.68);
  font-size: 12px;
  margin-bottom: 4px;
}

.edit-box input,
.edit-box textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(56, 189, 248, 0.24);
  border-radius: 6px;
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
  padding: 8px 10px;
  outline: none;
  font-family: inherit;
}

.edit-box textarea {
  resize: vertical;
  line-height: 1.5;
}

.compact-list,
.action-list {
  margin: 0;
  padding-left: 18px;
}

.compact-list li,
.action-list li {
  margin-bottom: 8px;
}

.compact-list span,
.compact-list em,
.condition-card span,
.communication-item span,
.communication-item em,
.shelter-card span,
.shelter-card em,
.route-card span,
.route-card p,
.route-card em,
.zone-card span,
.zone-card p,
.timeline-item span {
  display: block;
  color: rgba(255, 255, 255, 0.66);
  font-size: 12px;
  line-height: 1.55;
  font-style: normal;
}

.zone-card strong {
  display: inline-block;
  margin-bottom: 6px;
  font-size: 15px;
}

.condition-list {
  display: grid;
  gap: 10px;
}

.condition-card strong {
  display: block;
  margin-bottom: 6px;
  color: #ffffff;
}

.zone-red { border-color: rgba(248, 113, 113, 0.34); background: rgba(127, 29, 29, 0.18); }
.zone-orange { border-color: rgba(251, 146, 60, 0.34); background: rgba(124, 45, 18, 0.18); }
.zone-yellow { border-color: rgba(250, 204, 21, 0.34); background: rgba(113, 63, 18, 0.16); }
.zone-blue { border-color: rgba(56, 189, 248, 0.34); background: rgba(12, 74, 110, 0.18); }

.route-list,
.communication-list,
.timeline-list {
  display: grid;
  gap: 10px;
}

.route-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px;
  gap: 10px;
  border-color: rgba(99, 179, 237, 0.28);
}

.route-main strong,
.shelter-card strong,
.communication-item strong,
.timeline-item strong {
  color: #fff;
}

.route-meta {
  text-align: right;
}

.route-meta b {
  display: block;
  color: #67e8f9;
  font-size: 18px;
  margin: 4px 0;
}

.route-guard {
  grid-column: 1 / -1;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.62);
  font-size: 12px;
}

.timeline-item {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  gap: 8px;
  align-items: flex-start;
}

.timeline-item b {
  color: #67e8f9;
}

.data-table {
  width: 100%;
  margin-top: 8px;
  border-collapse: collapse;
  color: #fff;
  table-layout: fixed;
}

.data-table th,
.data-table td {
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  word-break: break-word;
  vertical-align: top;
}

.data-table th {
  background: rgba(56, 189, 248, 0.1);
  color: #b8ecff;
  font-weight: 600;
}

.table-sub {
  color: rgba(255, 255, 255, 0.56);
  font-size: 12px;
  line-height: 1.45;
}

.similar-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.similar-item p {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.5;
}

.empty-line,
.error-line {
  padding: 10px 12px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
}

.error-line {
  margin-top: 8px;
  color: #fecaca;
  background: rgba(239, 68, 68, 0.12);
}

.export-box {
  white-space: pre-wrap;
  max-height: 220px;
  overflow: auto;
  padding: 12px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.24);
  color: #d1fae5;
}

.gis-toolbar {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.gis-jump-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid rgba(56, 189, 248, 0.48);
  color: #b8ecff;
  background: rgba(56, 189, 248, 0.18);
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;
}

.gis-jump-btn:hover {
  background: rgba(56, 189, 248, 0.32);
}

.gis-detail-section {
  padding: 10px;
  border-radius: 8px;
  background: rgba(7, 30, 38, 0.62);
  border: 1px solid rgba(80, 216, 225, 0.16);
}

.gis-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
}

.gis-detail-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 10px;
}

.gis-detail-card strong {
  display: block;
  color: #ffffff;
  margin-bottom: 6px;
  font-size: 14px;
}

.gis-detail-card span,
.gis-detail-card p,
.gis-detail-card em {
  display: block;
  color: rgba(255, 255, 255, 0.66);
  font-size: 12px;
  line-height: 1.55;
  font-style: normal;
  margin-bottom: 2px;
}

.gis-detail-card p {
  color: rgba(255, 255, 255, 0.78);
  margin-top: 4px;
}

.gis-detail-card.risk-red {
  border-color: rgba(248, 113, 113, 0.32);
  background: rgba(127, 29, 29, 0.12);
}

.gis-detail-card.risk-orange {
  border-color: rgba(251, 146, 60, 0.32);
  background: rgba(124, 45, 18, 0.12);
}

.gis-detail-card.risk-yellow {
  border-color: rgba(250, 204, 21, 0.32);
  background: rgba(113, 63, 18, 0.10);
}

.gis-detail-card.zone-detail-red {
  border-color: rgba(248, 113, 113, 0.34);
  background: rgba(127, 29, 29, 0.14);
}

.gis-detail-card.zone-detail-orange {
  border-color: rgba(251, 146, 60, 0.34);
  background: rgba(124, 45, 18, 0.14);
}

.gis-detail-card.zone-detail-yellow {
  border-color: rgba(250, 204, 21, 0.34);
  background: rgba(113, 63, 18, 0.12);
}

.gis-detail-card.zone-detail-blue {
  border-color: rgba(56, 189, 248, 0.34);
  background: rgba(12, 74, 110, 0.14);
}

@media (max-width: 1180px) {
  .zone-grid,
  .similar-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .gis-detail-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .orb-business-panel {
    width: calc(100vw - 24px);
    height: calc(100vh - 96px);
    top: 72px;
    transform: translateX(-50%);
  }

  .event-summary,
  .sheet-grid,
  .two-col,
  .similar-list,
  .zone-grid,
  .shelter-grid,
  .route-card {
    grid-template-columns: 1fr;
  }

  .route-meta {
    text-align: left;
  }
}
</style>
